import React from "react";
import type { ApolloClient } from "@apollo/client";
import classnames from "classnames";
import moment from "moment";
import type Highcharts from "highcharts";
import { connect, type ConnectedProps } from "react-redux";
import { map, isEmpty, get, filter, flatten, camelCase } from "lodash";
import { Card, Form } from "react-bootstrap";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { formatCurrency, formatPercentage } from "utils/number";
import {
  getUserPositions,
  getUserPortfolio,
  getUserPerformance,
} from "actions/portfolioActions";
import { portfolioMetadataMap } from "utils/individualPortfolioHelper";
import PieChart from "components/highcharts/Pie";
import { createLoadingSelector } from "store/selectors";
import { portfolioConstants } from "actions/types";
import { categoryToColorMapping } from "utils/highcharts/config";
import IconSpinner from "components/IconSpinner";
import { getIraAccountIdSelector } from "store/selectors/user";
import type { RootState } from "types/state";

/** Shapes mirror the original `categoryPerformanceType` PropTypes. */
interface Returns {
  returnAmount: number;
  returnPercent: number;
}

interface AssetPerformance {
  returns: Returns;
  assetId: {
    id: string;
    name: string;
    symbol: string;
  };
  marketValue: number;
  categoryAllocation: number;
}

interface CategoryPerformance {
  category: string;
  returns: Returns;
  assetPerformances: AssetPerformance[];
  marketValue: number;
  allocation: number;
}

type CategoryColorKey = keyof typeof categoryToColorMapping;

const currentYear = moment.utc().format("YYYY");
const previousYear = moment.utc().subtract(1, "years").format("YYYY");
const twoYearsBeforeNow = moment.utc().subtract(2, "years").format("YYYY");
const threeYearsBeforeNow = moment.utc().subtract(3, "years").format("YYYY");

const timeOptions = [
  { value: "ALL_TIME", label: "All Time" },
  { value: "PAST_90_DAYS", label: "Past 90 Days" },
  { value: "PAST_30_DAYS", label: "Past 30 Days" },
  { value: "PAST_7_DAYS", label: "Past 7 Days" },
  { value: "CURRENT_DAY", label: "Current Day" },
  { value: currentYear, label: currentYear },
  { value: previousYear, label: previousYear },
  { value: twoYearsBeforeNow, label: twoYearsBeforeNow },
  { value: threeYearsBeforeNow, label: threeYearsBeforeNow },
];
const loadingSelector = createLoadingSelector([
  portfolioConstants.GET_USER_PORTFOLIO,
  portfolioConstants.GET_POSITIONS,
  portfolioConstants.GET_PERFORMANCE,
]);

const mapStateToProps = (state: RootState) => {
  const portfolioName = camelCase(
    get(state, "portfolios.activePortfolio.description")
  );
  return {
    positions: state.portfolios.positions,
    userPortfolio: state.portfolios.activePortfolio,
    positionsUpdatedAt: state.portfolios.positionsUpdatedAt,
    // redux slice types this `unknown[]`; cast to the shape the original
    // `categoryPerformanceType` PropTypes described and the component reads
    categoryPerformances: state.portfolios.categoryPerformances as
      | CategoryPerformance[]
      | null
      | undefined,
    performanceUpdatedAt: state.portfolios.performanceUpdatedAt,
    iraAccountId: getIraAccountIdSelector(state),
    isFetching: loadingSelector(state),
    createdAt: get(state.accounts, "iraAccounts[0].createdAt", {}),
    portfolioName,
  };
};

const mapDispatchToProps = {
  getUserPositions,
  getUserPortfolio,
  getUserPerformance,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = WithApolloClient<ConnectedProps<typeof connector>>;

interface State {
  activeSeriesCategory: string;
  selectedTimePeriod: string;
}

class PerformanceTable extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      activeSeriesCategory: "",
      selectedTimePeriod: "CURRENT_DAY",
    };
  }

  componentDidMount() {
    void this.props.getUserPositions(
      this.props.client as ApolloClient<object>,
      this.props.iraAccountId as string
    );
    void this.props.getUserPortfolio(
      this.props.client as ApolloClient<object>,
      this.props.iraAccountId
    );
    void this._getPerformance();
  }
  _getPerformance = () => {
    const { startTime, endTime } = this._timeSlicedReturns();
    return this.props.getUserPerformance(
      this.props.client as ApolloClient<object>,
      this.props.iraAccountId as string,
      startTime.toISOString(),
      endTime.toISOString()
    );
  };
  _getPortfolioChartConfig = (): Highcharts.Options => {
    // Highcharts binds `this` inside formatter/point callbacks to its own
    // contexts, so the component is captured via this alias (cannot become an
    // arrow without losing the Highcharts `this`) — preserved from the original
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const reactInstance = this;
    // the literal carries non-Highcharts extras (e.g. pie width/height, a
    // custom tooltip-formatter `this`) that are inert under the downstream
    // merge — cast the whole override rather than reshaping it
    return {
      tooltip: {
        // the original (JS) formatter reads `this.point.assetPerformances`
        // off the Highcharts tooltip context; type `this` to that shape
        formatter(this: { point: { assetPerformances: AssetPerformance[] } }) {
          return `<div> ${map(
            this.point.assetPerformances,
            (p) =>
              `<div> <span style="color:blue; font-weight: 600;  " > ${formatPercentage(
                p.categoryAllocation / 100
              )} : </span> <span style=" font-weight: 500; color: #202546;  "> ${
                p.assetId.symbol
              } </span> ${p.assetId.name}  </div>`
          ).join()} </div>`;
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: false,
          showInLegend: false,
          innerSize: "55%",
          width: 200,
          height: 200,
          point: {
            events: {
              mouseOver(this: Highcharts.Point) {
                reactInstance.setState({ activeSeriesCategory: this.name });
              },
              mouseOut() {
                reactInstance.setState({ activeSeriesCategory: "" });
              },
            },
          },
          dataLabels: {
            enabled: false,
            distance: -14,
            color: "white",
            style: {
              fontweight: "bold",
              fontFamily:
                '"Graphik", "Open Sans", "Helvetica Neue", Arial, sans-serif',
            },
          },
        },
      },
    } as unknown as Highcharts.Options;
  };

  _buildRows = () => {
    const tableRows = map(this.props.categoryPerformances, (category) => {
      const isActiveSeries =
        this.state.activeSeriesCategory === category.category;
      if (category.category === "Total") {
        return (
          <tr
            key={category.category}
            className={classnames({
              "is-selected": isActiveSeries,
            })}
          >
            <td className="category-total">{category.category}</td>
            <td className="category-total-subclass">
              {formatCurrency(category.marketValue)}
            </td>
            <td className="category-total-subclass">
              {formatCurrency(category.returns.returnAmount)}
            </td>
            <td className="category-total-subclass">
              {formatPercentage(category.returns.returnPercent / 100)}
            </td>
          </tr>
        );
      } else {
        return map(category.assetPerformances, (asset) => {
          return (
            <tr
              key={get(asset, "assetId.id")}
              className={classnames({
                "is-selected": isActiveSeries,
              })}
            >
              <td>
                <div className="asset">
                  <span
                    style={{
                      backgroundColor:
                        categoryToColorMapping[
                          category.category as CategoryColorKey
                        ],
                    }}
                    className="asset-box"
                  ></span>
                  <p className="asset-name"> {category.category}</p>
                </div>
                <div className="asset-symbol">
                  <p className="symbol">{asset.assetId.symbol}</p>
                </div>
              </td>
              <td>
                <div>{formatCurrency(asset.marketValue)}</div>
              </td>
              <td>{formatCurrency(asset.returns.returnAmount)}</td>
              <td>{formatPercentage(asset.returns.returnPercent / 100)}</td>
            </tr>
          );
        });
      }
    });
    return flatten(tableRows);
  };

  _buildChartData(): Highcharts.SeriesOptionsType[] {
    const categoriesWithoutTotal = filter(
      this.props.categoryPerformances,
      (category) => category.category !== "Total"
    );
    return [
      {
        colorByPoint: true,
        size: 275,
        data: map(categoriesWithoutTotal, (data) => {
          return {
            name: data.category,
            y: data.allocation,
            assetPerformances: data.assetPerformances,
            color: categoryToColorMapping[data.category as CategoryColorKey],
          };
        }),
        // custom `assetPerformances` per point (read by the tooltip formatter)
        // is inert to Highcharts' series typing
      },
    ] as unknown as Highcharts.SeriesOptionsType[];
  }

  _timeSlicedReturns() {
    let startTime: moment.Moment;
    const endTime = moment.utc();

    //Default Statement for time sliced returns will be "CURRENT_DAY"

    switch (this.state.selectedTimePeriod) {
      case "ALL_TIME":
        startTime = moment.utc(this.props.createdAt);
        break;
      case "PAST_90_DAYS":
        startTime = moment.utc().subtract(90, "days");
        break;
      case "PAST_30_DAYS":
        startTime = moment.utc().subtract(30, "days");
        break;
      case "PAST_7_DAYS":
        startTime = moment.utc().subtract(7, "days");
        break;
      case "CURRENT_DAY":
        startTime = moment.utc().startOf("day");
        break;
      default:
        startTime = moment.utc(`${this.state.selectedTimePeriod}-01-01`);
    }
    return { startTime, endTime };
  }

  render() {
    const positionRows = this._buildRows();

    const { startTime, endTime } = this._timeSlicedReturns();

    if (isEmpty(positionRows)) return null;
    // PRESERVED BUG (conversion-only): redux types performanceUpdatedAt as
    // `string | null | undefined`, but the original reads `.endTime` off it
    // (yielding undefined for a string, throwing for null/undefined). Cast to
    // mirror the access exactly rather than guarding it.
    const updatedAt = moment(
      (this.props.performanceUpdatedAt as unknown as { endTime?: string })
        .endTime
    ).format("MMMM DD, YYYY h:mm A ");

    const isFetching = this.props.isFetching;
    return (
      <>
        {/* <div>
          <h2 className="current-portfolio">Investments</h2>
        </div> */}
        <Card className="dashboard-widget performance-table">
          {isFetching && <IconSpinner centered />}
          {!isFetching && (
            <>
              <div className="current-portfolio-header">
                <p className="user-portfolio">
                  Current Portfolio:{" "}
                  {get(
                    portfolioMetadataMap,
                    `${this.props.portfolioName}.portfolio`
                  )}
                </p>
                <div className="time-slice">
                  <span className="time-slice-dates">
                    <p className="dates">
                      {startTime.format("MMMM DD, YYYY")} -{" "}
                      {endTime.format("MMMM DD, YYYY")}
                    </p>
                  </span>

                  <span>
                    <Form.Select
                      className="time-slice-btn"
                      id="inlineFormCustomSelect"
                      value={this.state.selectedTimePeriod}
                      onChange={(e) => {
                        this.setState(
                          { selectedTimePeriod: e.target.value },
                          () => this._getPerformance()
                        );
                      }}
                    >
                      {map(timeOptions, (opt) => (
                        <option value={opt.value} key={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                  </span>
                </div>
              </div>

              <div className="preformance-content">
                <div className="pie-chart">
                  <PieChart
                    chartData={this._buildChartData()}
                    extendedConfig={this._getPortfolioChartConfig()}
                  />
                </div>

                <table className="positions-table">
                  <thead>
                    <tr>
                      <th>Holdings</th>
                      <th>Current Value</th>
                      <th
                        // colSpan expects number; original passed the string
                        // "2" and React coerces it at runtime — preserved
                        // @ts-expect-error string colSpan preserved verbatim
                        colSpan="2"
                      >
                        Total Change
                      </th>
                    </tr>
                  </thead>
                  <tbody>{positionRows}</tbody>
                </table>
              </div>

              <div className="time-stamp">
                <span className="circle"></span>
                <p className="as-of">Data current as of {updatedAt}</p>
              </div>
            </>
          )}
        </Card>
      </>
    );
  }
}

export default connector(withApollo<Props>(PerformanceTable));
